import * as React from "react";
import { cn } from "@/lib/utils";

export type ButtonVariant =
  | "default"
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "destructive";

export type ButtonSize = "default" | "sm" | "lg" | "icon";

const baseClasses =
  "inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/60 disabled:cursor-not-allowed disabled:opacity-60";

const variantClasses: Record<ButtonVariant, string> = {
  default: "bg-ink text-canvas shadow-panel hover:bg-black",
  primary: "bg-ink text-canvas shadow-panel hover:bg-black",
  secondary:
    "bg-white/70 text-ink ring-1 ring-ink/10 hover:bg-white disabled:bg-white/70",
  outline:
    "bg-white/55 text-ink ring-1 ring-ink/10 hover:bg-white/80 disabled:bg-white/55",
  ghost: "bg-transparent text-ink hover:bg-ink/5",
  destructive:
    "bg-clay text-canvas shadow-panel hover:bg-clay/90 disabled:bg-clay/60",
};

const sizeClasses: Record<ButtonSize, string> = {
  default: "px-5 py-3",
  sm: "px-4 py-2 text-xs",
  lg: "px-6 py-3.5 text-base",
  icon: "h-11 w-11 rounded-full p-0",
};

export function buttonVariants({
  className,
  size = "default",
  variant = "default",
}: {
  className?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
} = {}) {
  return cn(baseClasses, variantClasses[variant], sizeClasses[size], className);
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      size = "default",
      type = "button",
      variant = "default",
      ...props
    },
    ref,
  ) => {
    const classes = buttonVariants({ className, size, variant });

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{
          className?: string;
        }>,
        {
          ...props,
          className: cn(classes, children.props.className),
        },
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
